import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import breakpoints from '@/breakpoints';
import { toCamelCase } from '@/utils';

const GOOGLE_ADS_IDENTIFIERS = ['gclid', 'gbraid', 'wbraid'];

/**
 * Redux useSelector() with shallowEqual comparison
 */
export function useShallowEqualSelector(selector) {
	return useSelector(selector, shallowEqual);
}

export function useScrollToTop(options) {
	useEffect(() => {
		window.scrollTo({ top: 0, ...options });
	}, []);
}

function parseQuery(query) {
	const breakPointRegex = /^(xs|sm|md|lg|xl|2xl)(Up|Down)?$/;

	if (!breakPointRegex.test(query)) {
		return query;
	}

	const [, breakpoint, method] = query.match(breakPointRegex);

	return method === 'Up'
		? `(width >= ${breakpoints[breakpoint]})`
		: `(width < ${breakpoints[breakpoint]})`;
}

/**
 * @param {string} query Could be a breakpoint or a media query
 * @example
 * useMediaQuery('smUp') // (width >= 640px)
 * useMediaQuery('sm') // (width < 640px)
 */
export function useMediaQuery(query) {
	const subscribe = useCallback(
		callback => {
			const matchMedia = window.matchMedia(parseQuery(query));

			matchMedia.addEventListener('change', callback);
			return () => {
				matchMedia.removeEventListener('change', callback);
			};
		},
		[query]
	);

	const getSnapshot = () => {
		return window.matchMedia(parseQuery(query)).matches;
	};

	return useSyncExternalStore(subscribe, getSnapshot);
}

export function useQueryParams() {
	return new URLSearchParams(useLocation().search);
}

export function useIsCrawler() {
	const queryParams = useQueryParams();
	return queryParams.get('crawler') === 'true';
}

export function useGetErrorMessage(mapping) {
	const { t, i18n } = useTranslation();

	return error => {
		if (!error) {
			return '';
		}

		const errorCode = toCamelCase(error.data?.errorCode || '');

		if (mapping?.[errorCode]) {
			return mapping[errorCode];
		}

		if (errorCode && i18n.exists(`errors.${errorCode}`)) {
			return t(`errors.${errorCode}`);
		}

		return t('errors.generic');
	};
}

export function useDialog(initialState = false) {
	const [open, setOpen] = useState(initialState);

	const handleOpen = useCallback(() => {
		setOpen(true);
	}, []);

	const handleClose = useCallback(() => {
		setOpen(false);
	}, []);

	return { handleClose, handleOpen, open };
}

export function useSaveGclid() {
	const [queryParams] = useSearchParams();

	useEffect(() => {
		let key;
		let value;
		for (const identifier of GOOGLE_ADS_IDENTIFIERS) {
			if (queryParams.has(identifier)) {
				key = identifier;
				value = queryParams.get(identifier);
				break;
			}
		}

		if (key) {
			const params = ['googleAdId', `${key}:${value}`];

			localStorage.setItem(...params);
			localStorage.setItem('with_gclid', 1);
		}
	}, []);
}
